/** Default / helper layout for freeform category pages. */

export const DEFAULT_BLOCK_HEIGHT = 220
export const BLOCK_V_GAP = 12
export const TABLE_STACK_GAP = 28 // extra space between stacked data tables
export const BLOCK_H_GAP = 2 // percent
export const ROW_CLUSTER_PX = 40

export function newBlockId(prefix = 'block') {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`
}

export function defaultBlockTitle(type) {
  return type === 'table' ? 'Data table' : 'Notes'
}

export function blockDisplayTitle(block) {
  const title = typeof block?.title === 'string' ? block.title.trim() : ''
  return title || defaultBlockTitle(block?.type)
}

export function defaultLayoutForCategory(category) {
  if (!category || category.kind === 'devices') return { version: 1, blocks: [] }

  // Honour an explicit page_layout (including empty blocks after Remove).
  if (category.page_layout && Array.isArray(category.page_layout.blocks)) {
    return {
      version: category.page_layout.version || 1,
      blocks: category.page_layout.blocks.map((b) => {
        const block = { ...b }
        if (!block.title) {
          block.title = defaultBlockTitle(block.type)
        }
        // Legacy main table often has empty columns[] — use category fields.
        if (
          block.type === 'table' &&
          !(block.columns && block.columns.length) &&
          category.columns?.length
        ) {
          block.columns = category.columns.map((c) => ({ ...c }))
        }
        return block
      }),
    }
  }

  const blocks = []
  if (category.kind === 'rich') {
    blocks.push({
      id: 'main-rich',
      type: 'rich',
      title: defaultBlockTitle('rich'),
      x: 0,
      y: 0,
      w: 100,
    })
    if (category.columns?.length) {
      blocks.push({
        id: 'main-table',
        type: 'table',
        title: defaultBlockTitle('table'),
        x: 0,
        y: 240,
        w: 100,
        columns: category.columns.map((c) => ({ ...c })),
      })
    }
  } else if (category.kind === 'table') {
    blocks.push({
      id: 'main-table',
      type: 'table',
      title: defaultBlockTitle('table'),
      x: 0,
      y: 0,
      w: 100,
      columns: (category.columns || []).map((c) => ({ ...c })),
    })
  }
  return { version: 1, blocks }
}

export function blockHeight(block, heights) {
  return heights?.[block.id] || (block.type === 'table' ? 260 : DEFAULT_BLOCK_HEIGHT)
}

export function blocksOverlap(a, b, heights) {
  const ax1 = Number(a.x) || 0
  const ax2 = ax1 + (Number(a.w) || 100)
  const bx1 = Number(b.x) || 0
  const bx2 = bx1 + (Number(b.w) || 100)
  const ay1 = Number(a.y) || 0
  const ay2 = ay1 + blockHeight(a, heights)
  const by1 = Number(b.y) || 0
  const by2 = by1 + blockHeight(b, heights)
  const hOverlap = ax1 < bx2 - 0.5 && ax2 > bx1 + 0.5
  const vOverlap = ay1 < by2 + BLOCK_V_GAP && ay2 + BLOCK_V_GAP > by1
  return hOverlap && vOverlap
}

/** Push overlapping blocks apart (prefer moving the lower one down). */
export function resolveOverlaps(blocks, heights) {
  const next = blocks.map((b) => ({
    ...b,
    x: Math.max(0, Math.min(100 - (Number(b.w) || 100), Number(b.x) || 0)),
    w: Math.max(20, Math.min(100, Number(b.w) || 100)),
    y: Math.max(0, Number(b.y) || 0),
  }))

  for (let iter = 0; iter < 40; iter += 1) {
    let moved = false
    for (let i = 0; i < next.length; i += 1) {
      for (let j = i + 1; j < next.length; j += 1) {
        if (!blocksOverlap(next[i], next[j], heights)) continue
        const a = next[i]
        const b = next[j]
        // Prefer side-by-side fix if both fit on one row with room
        if (Math.abs((Number(a.y) || 0) - (Number(b.y) || 0)) < 40) {
          const left = (Number(a.x) || 0) <= (Number(b.x) || 0) ? a : b
          const right = left === a ? b : a
          const need = (Number(left.w) || 50) + BLOCK_H_GAP + (Number(right.w) || 50)
          if (need <= 100.5) {
            left.x = 0
            right.x = (Number(left.w) || 50) + BLOCK_H_GAP
            if (right.x + (Number(right.w) || 50) > 100) {
              right.w = Math.max(20, 100 - right.x)
            }
            moved = true
            continue
          }
        }
        // Otherwise stack: move the lower (or second) block down
        const upper = (Number(a.y) || 0) <= (Number(b.y) || 0) ? a : b
        const lower = upper === a ? b : a
        const gap =
          upper.type === 'table' && lower.type === 'table' ? TABLE_STACK_GAP : BLOCK_V_GAP
        const newY = (Number(upper.y) || 0) + blockHeight(upper, heights) + gap
        if (Math.abs((Number(lower.y) || 0) - newY) > 0.5) {
          lower.y = newY
          moved = true
        }
      }
    }
    if (!moved) break
  }
  return packBlocksVertically(next, heights)
}

/** Stack rows tightly using measured heights while keeping side-by-side items on one row. */
export function packBlocksVertically(blocks, heights) {
  if (!blocks.length) return blocks
  const sorted = [...blocks].sort(
    (a, b) => (Number(a.y) || 0) - (Number(b.y) || 0) || (Number(a.x) || 0) - (Number(b.x) || 0),
  )
  const rows = []
  for (const b of sorted) {
    const last = rows[rows.length - 1]
    if (last && Math.abs((Number(b.y) || 0) - last.anchorY) <= ROW_CLUSTER_PX) {
      last.items.push(b)
    } else {
      rows.push({ anchorY: Number(b.y) || 0, items: [b] })
    }
  }
  let cursorY = 0
  const packed = []
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i]
    const rowH = Math.max(...row.items.map((b) => blockHeight(b, heights)))
    for (const b of row.items) {
      packed.push({ ...b, y: cursorY })
    }
    const next = rows[i + 1]
    const bothTables =
      next &&
      row.items.every((b) => b.type === 'table') &&
      next.items.every((b) => b.type === 'table')
    cursorY += rowH + (bothTables ? TABLE_STACK_GAP : BLOCK_V_GAP)
  }
  return packed
}

/** Cluster blocks into horizontal rows (same logic as packBlocksVertically). */
export function groupBlocksIntoRows(blocks) {
  const sorted = [...blocks].sort(
    (a, b) => (Number(a.y) || 0) - (Number(b.y) || 0) || (Number(a.x) || 0) - (Number(b.x) || 0),
  )
  const rows = []
  for (const b of sorted) {
    const last = rows[rows.length - 1]
    if (last && Math.abs((Number(b.y) || 0) - last.anchorY) <= ROW_CLUSTER_PX) {
      last.items.push(b)
    } else {
      rows.push({ anchorY: Number(b.y) || 0, items: [b] })
    }
  }
  return rows
}

/**
 * Move the row containing blockId up (-1) or down (+1) in the stack, then pack.
 * Side-by-side blocks in the same row move together.
 */
export function moveBlockRow(blocks, blockId, direction, heights) {
  if (!blocks?.length || !blockId || (direction !== -1 && direction !== 1)) {
    return blocks
  }
  const rows = groupBlocksIntoRows(blocks)
  const rowIdx = rows.findIndex((r) => r.items.some((item) => item.id === blockId))
  if (rowIdx < 0) return blocks
  const targetIdx = rowIdx + direction
  if (targetIdx < 0 || targetIdx >= rows.length) return blocks

  const nextRows = [...rows]
  const tmp = nextRows[rowIdx]
  nextRows[rowIdx] = nextRows[targetIdx]
  nextRows[targetIdx] = tmp

  // Assign increasing y so pack/resolve honour the new row order.
  let cursorY = 0
  const reordered = []
  for (const row of nextRows) {
    for (const item of row.items) {
      reordered.push({ ...item, y: cursorY })
    }
    cursorY += 2000
  }
  return resolveOverlaps(reordered, heights)
}

export function canMoveBlockRow(blocks, blockId, direction) {
  if (!blocks?.length || !blockId || (direction !== -1 && direction !== 1)) {
    return false
  }
  const rows = groupBlocksIntoRows(blocks)
  const rowIdx = rows.findIndex((r) => r.items.some((item) => item.id === blockId))
  if (rowIdx < 0) return false
  const targetIdx = rowIdx + direction
  return targetIdx >= 0 && targetIdx < rows.length
}

export function nextBlockY(blocks, heights) {
  if (!blocks.length) return 0
  const lastIsTable = blocks.some((b) => {
    const bottom = (Number(b.y) || 0) + blockHeight(b, heights)
    const maxBottom = Math.max(
      ...blocks.map((x) => (Number(x.y) || 0) + blockHeight(x, heights)),
    )
    return Math.abs(bottom - maxBottom) < 1 && b.type === 'table'
  })
  return Math.max(
    ...blocks.map(
      (b) =>
        (Number(b.y) || 0) +
        blockHeight(b, heights) +
        (lastIsTable ? TABLE_STACK_GAP : BLOCK_V_GAP),
    ),
  )
}

/** Place a new rich block beside an existing one when there is room. */
export function placeRichBeside(blocks, heights) {
  const richBlocks = blocks.filter((b) => b.type === 'rich')
  const anchor =
    [...richBlocks].sort(
      (a, b) => (Number(b.y) || 0) - (Number(a.y) || 0) || (Number(b.x) || 0) - (Number(a.x) || 0),
    )[0] || null

  const newBlock = {
    id: newBlockId('rich'),
    type: 'rich',
    title: defaultBlockTitle('rich'),
    x: 0,
    y: nextBlockY(blocks, heights),
    w: 100,
    content_html: '<p></p>',
  }

  if (!anchor) {
    return resolveOverlaps([...blocks, newBlock], heights)
  }

  const gap = BLOCK_H_GAP
  const half = (100 - gap) / 2

  // Full-width anchor → split into a side-by-side pair
  if ((Number(anchor.w) || 100) >= 85 && (Number(anchor.x) || 0) <= 5) {
    const updated = blocks.map((b) =>
      b.id === anchor.id ? { ...b, x: 0, w: half } : b,
    )
    newBlock.x = half + gap
    newBlock.y = Number(anchor.y) || 0
    newBlock.w = half
    return resolveOverlaps([...updated, newBlock], heights)
  }

  // Room to the right of anchor
  const rightX = (Number(anchor.x) || 0) + (Number(anchor.w) || 100) + gap
  const avail = 100 - rightX
  if (avail >= 28) {
    newBlock.x = rightX
    newBlock.y = Number(anchor.y) || 0
    newBlock.w = Math.min(half, avail)
    return resolveOverlaps([...blocks, newBlock], heights)
  }

  // Room to the left
  if ((Number(anchor.x) || 0) >= 28) {
    newBlock.x = 0
    newBlock.y = Number(anchor.y) || 0
    newBlock.w = Math.min(half, (Number(anchor.x) || 0) - gap)
    return resolveOverlaps([...blocks, newBlock], heights)
  }

  return resolveOverlaps([...blocks, newBlock], heights)
}
